// src/pages/Officer/OfficerProfile.jsx
import React, { useState, useContext } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Stack,
  Grid,
  TextField,
  Button,
  Avatar,
  Divider,
  Switch,
  FormControlLabel,
  Alert,
  Chip,
} from '@mui/material';
import {
  Edit,
  Save,
  Cancel,
  Person,
  Email,
  Badge,
  CalendarToday,
  Notifications,
  Security,
  CheckCircle,
} from '@mui/icons-material';
import AuthContext from '../../contexts/AuthContext';

const OfficerProfile = () => {
  const { user } = useContext(AuthContext);
  const [editMode, setEditMode] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [formData, setFormData] = useState({
    fullName: user?.full_name || '',
    email: user?.email || '',
    username: user?.username || '',
  });
  const [notifications, setNotifications] = useState({
    email: true,
    push: true,
    sms: false,
  });

  const handleSave = () => {
    // Simulate save
    setEditMode(false);
    setShowSuccess(true);
    setTimeout(() => setShowSuccess(false), 3000);
  };

  const handleCancel = () => {
    setEditMode(false);
    setFormData({
      fullName: user?.full_name || '',
      email: user?.email || '',
      username: user?.username || '',
    });
  };

  return (
    <Box sx={{ py: 4, px: { xs: 2, sm: 3, md: 4 } }}>
      <Container maxWidth="lg">
        {/* Header */}
        <Stack spacing={3} sx={{ mb: 4 }}>
          <Box>
            <Typography
              variant="h4"
              sx={{ fontWeight: 700, color: '#1e293b', mb: 0.5 }}
            >
              My Profile
            </Typography>
            <Typography variant="body1" sx={{ color: '#64748b' }}>
              Manage your account settings and preferences
            </Typography>
          </Box>

          {showSuccess && (
            <Alert
              icon={<CheckCircle />}
              severity="success"
              sx={{ borderRadius: 2 }}
            >
              Profile updated successfully!
            </Alert>
          )}
        </Stack>

        <Grid container spacing={3}>
          {/* Profile Info Card */}
          <Grid item xs={12} md={4}>
            <Paper
              sx={{
                p: 3,
                border: '1px solid #e2e8f0',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
                textAlign: 'center',
              }}
            >
              <Avatar
                sx={{
                  width: 120,
                  height: 120,
                  margin: '0 auto',
                  mb: 2,
                  fontSize: '3rem',
                  fontWeight: 700,
                  background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                  boxShadow: '0 8px 24px rgba(16, 185, 129, 0.3)',
                }}
              >
                {user?.full_name?.charAt(0) || user?.username?.charAt(0) || 'O'}
              </Avatar>
              <Typography
                variant="h5"
                sx={{ fontWeight: 700, color: '#1e293b', mb: 1 }}
              >
                {user?.full_name}
              </Typography>
              <Chip
                label="OFFICER"
                size="small"
                sx={{
                  fontWeight: 700,
                  background: 'rgba(16, 185, 129, 0.1)',
                  color: '#10b981',
                  border: '1px solid rgba(16, 185, 129, 0.3)',
                  mb: 2,
                }}
              />
              <Typography variant="body2" sx={{ color: '#64748b' }}>
                @{user?.username}
              </Typography>

              <Divider sx={{ my: 3 }} />

              <Stack spacing={2} alignItems="flex-start">
                <Stack direction="row" spacing={2} alignItems="center">
                  <CalendarToday sx={{ color: '#64748b', fontSize: 20 }} />
                  <Box>
                    <Typography variant="caption" sx={{ color: '#64748b' }}>
                      Member Since
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 600, color: '#1e293b' }}
                    >
                      {new Date(user?.created_at || Date.now()).toLocaleDateString()}
                    </Typography>
                  </Box>
                </Stack>
                <Stack direction="row" spacing={2} alignItems="center">
                  <Badge sx={{ color: '#64748b', fontSize: 20 }} />
                  <Box>
                    <Typography variant="caption" sx={{ color: '#64748b' }}>
                      Last Login
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 600, color: '#1e293b' }}
                    >
                      {new Date(user?.last_login || Date.now()).toLocaleDateString()}
                    </Typography>
                  </Box>
                </Stack>
              </Stack>
            </Paper>
          </Grid>

          {/* Profile Details */}
          <Grid item xs={12} md={8}>
            <Stack spacing={3}>
              {/* Personal Information */}
              <Paper
                sx={{
                  p: 3,
                  border: '1px solid #e2e8f0',
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
                }}
              >
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                  sx={{ mb: 3 }}
                >
                  <Typography
                    variant="h6"
                    sx={{ fontWeight: 700, color: '#1e293b' }}
                  >
                    Personal Information
                  </Typography>
                  {!editMode ? (
                    <Button
                      startIcon={<Edit />}
                      onClick={() => setEditMode(true)}
                      sx={{
                        color: '#10b981',
                        '&:hover': { background: 'rgba(16, 185, 129, 0.1)' },
                      }}
                    >
                      Edit
                    </Button>
                  ) : (
                    <Stack direction="row" spacing={1}>
                      <Button
                        startIcon={<Cancel />}
                        onClick={handleCancel}
                        sx={{ color: '#64748b' }}
                      >
                        Cancel
                      </Button>
                      <Button
                        startIcon={<Save />}
                        variant="contained"
                        onClick={handleSave}
                        sx={{
                          background:
                            'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                          '&:hover': {
                            background:
                              'linear-gradient(135deg, #059669 0%, #047857 100%)',
                          },
                        }}
                      >
                        Save
                      </Button>
                    </Stack>
                  )}
                </Stack>

                <Stack spacing={3}>
                  <TextField
                    fullWidth
                    label="Full Name"
                    value={formData.fullName}
                    onChange={(e) =>
                      setFormData({ ...formData, fullName: e.target.value })
                    }
                    disabled={!editMode}
                    InputProps={{
                      startAdornment: <Person sx={{ color: '#94a3b8', mr: 1 }} />,
                    }}
                  />
                  <TextField
                    fullWidth
                    label="Email"
                    value={formData.email}
                    onChange={(e) =>
                      setFormData({ ...formData, email: e.target.value })
                    }
                    disabled={!editMode}
                    InputProps={{
                      startAdornment: <Email sx={{ color: '#94a3b8', mr: 1 }} />,
                    }}
                  />
                  <TextField
                    fullWidth
                    label="Username"
                    value={formData.username}
                    disabled
                    InputProps={{
                      startAdornment: <Person sx={{ color: '#94a3b8', mr: 1 }} />,
                    }}
                    helperText="Username cannot be changed"
                  />
                </Stack>
              </Paper>

              {/* Notification Settings */}
              <Paper
                sx={{
                  p: 3,
                  border: '1px solid #e2e8f0',
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
                }}
              >
                <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
                  <Notifications sx={{ color: '#10b981' }} />
                  <Typography
                    variant="h6"
                    sx={{ fontWeight: 700, color: '#1e293b' }}
                  >
                    Notification Preferences
                  </Typography>
                </Stack>

                <Stack spacing={2}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={notifications.email}
                        onChange={(e) =>
                          setNotifications({
                            ...notifications,
                            email: e.target.checked,
                          })
                        }
                        sx={{
                          '& .MuiSwitch-switchBase.Mui-checked': {
                            color: '#10b981',
                          },
                          '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track':
                            {
                              backgroundColor: '#10b981',
                            },
                        }}
                      />
                    }
                    label={
                      <Box>
                        <Typography
                          variant="body1"
                          sx={{ fontWeight: 600, color: '#1e293b' }}
                        >
                          Email Notifications
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#64748b' }}>
                          Receive alerts and updates via email
                        </Typography>
                      </Box>
                    }
                  />
                  <Divider />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={notifications.push}
                        onChange={(e) =>
                          setNotifications({
                            ...notifications,
                            push: e.target.checked,
                          })
                        }
                        sx={{
                          '& .MuiSwitch-switchBase.Mui-checked': {
                            color: '#10b981',
                          },
                          '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track':
                            {
                              backgroundColor: '#10b981',
                            },
                        }}
                      />
                    }
                    label={
                      <Box>
                        <Typography
                          variant="body1"
                          sx={{ fontWeight: 600, color: '#1e293b' }}
                        >
                          Push Notifications
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#64748b' }}>
                          Get instant notifications on your device
                        </Typography>
                      </Box>
                    }
                  />
                  <Divider />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={notifications.sms}
                        onChange={(e) =>
                          setNotifications({
                            ...notifications,
                            sms: e.target.checked,
                          })
                        }
                        sx={{
                          '& .MuiSwitch-switchBase.Mui-checked': {
                            color: '#10b981',
                          },
                          '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track':
                            {
                              backgroundColor: '#10b981',
                            },
                        }}
                      />
                    }
                    label={
                      <Box>
                        <Typography
                          variant="body1"
                          sx={{ fontWeight: 600, color: '#1e293b' }}
                        >
                          SMS Notifications
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#64748b' }}>
                          Receive critical alerts via SMS
                        </Typography>
                      </Box>
                    }
                  />
                </Stack>
              </Paper>

              {/* Security */}
              <Paper
                sx={{
                  p: 3,
                  border: '1px solid #e2e8f0',
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
                }}
              >
                <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
                  <Security sx={{ color: '#10b981' }} />
                  <Typography
                    variant="h6"
                    sx={{ fontWeight: 700, color: '#1e293b' }}
                  >
                    Security
                  </Typography>
                </Stack>

                <Button
                  variant="outlined"
                  fullWidth
                  sx={{
                    borderColor: '#e2e8f0',
                    color: '#64748b',
                    py: 1.5,
                    '&:hover': {
                      borderColor: '#10b981',
                      color: '#10b981',
                      background: 'rgba(16, 185, 129, 0.05)',
                    },
                  }}
                >
                  Change Password
                </Button>
              </Paper>
            </Stack>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default OfficerProfile;
